import {
  findFacilityById,
  findPartnerById,
  findProceduresBySlugs,
  findProfessionalById,
  findSchedulesByProfessionalId,
} from '@/app/(main)/(pages)/_services/search-api/search-api.service';
import { hydrateById, uniqueIds } from '@/app/(main)/(pages)/_services/search-api/hydrate-by-id.util';
import { mapToSearchPartnerData } from '@/app/(main)/(pages)/_services/search-api/map-to-search-partner-data.util';
import { toProcedureFallback } from '@/app/(main)/(pages)/_services/search-api/procedure-fallback.util';
import {
  SearchApiFacility,
  SearchApiPartner,
  SearchApiProcedure,
  SearchApiProcedureGroup,
  SearchApiProcedurePricing,
  SearchApiProfessional,
  SearchApiScheduleGroup,
  SearchApiServiceChannel,
} from '@/app/(main)/(pages)/_types/search-api-response.type';
import { SearchPartnerData, SearchType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { partnerBranchDisplayName } from '@/utils/formatters/branch';
import { getLogger } from '@/logger/logger';

export const PROFESSIONAL_PROCEDURES_PAGE_SIZE = 10;

export interface ProfessionalProcedurePlace {
  id: string;
  name: string;
  totalPrice: number;
  schedule: SearchPartnerData;
}

export interface ProfessionalProcedureGroup {
  id: string;
  name: string;
  places: ProfessionalProcedurePlace[];
}

export interface GetProfessionalProceduresParams {
  professionalId: string;
  serviceChannel: SearchApiServiceChannel;
  page?: number;
  limit?: number;
}

export interface GetProfessionalProceduresResult {
  procedures: ProfessionalProcedureGroup[];
  hasMore: boolean;
}

const EMPTY_RESULT: GetProfessionalProceduresResult = { procedures: [], hasMore: false };

function toPricing(group: SearchApiProcedureGroup): SearchApiProcedurePricing {
  const { promotion } = group.procedure;

  return {
    partnerFacilityId: group.partnerFacilityId ?? '',
    procedures: [
      {
        procedureId: group.procedure.id,
        price: group.procedure.price ?? 0,
        promotion: promotion
          ? { id: promotion.id, originalPrice: promotion.originalPrice, price: promotion.price }
          : null,
      },
    ],
  };
}

function toScheduleGroup(group: SearchApiProcedureGroup): SearchApiScheduleGroup {
  return {
    professionalId: group.professionalId,
    partnerId: group.partnerId,
    partnerFacilityId: group.partnerFacilityId,
    schedulesByScheduleType: group.schedulesByScheduleType,
    procedures: [{ id: group.procedure.id, name: group.procedure.name, slug: group.procedure.slug }],
    distance: null,
    score: 0,
    timeZone: group.timeZone,
    timeOffset: group.timeOffset,
  };
}

interface BuildPlaceArgs {
  group: SearchApiProcedureGroup;
  procedure: SearchApiProcedure;
  professional: SearchApiProfessional | null;
  partner: SearchApiPartner | null;
  facility: SearchApiFacility | null;
  searchType?: SearchType;
}

function buildPlace({
  group,
  procedure,
  professional,
  partner,
  facility,
  searchType,
}: BuildPlaceArgs): ProfessionalProcedurePlace {
  const schedule = mapToSearchPartnerData({
    group: toScheduleGroup(group),
    procedures: [procedure],
    professional,
    partner,
    facility,
    rating: null,
    pricing: toPricing(group),
    searchType,
  });

  return {
    id: group.partnerFacilityId ?? '',
    name: partnerBranchDisplayName(schedule.partner),
    totalPrice: group.procedure.price ?? 0,
    schedule,
  };
}

function groupByProcedure(places: { group: SearchApiProcedureGroup; place: ProfessionalProcedurePlace }[]) {
  const byProcedureId = new Map<string, ProfessionalProcedureGroup>();

  places.forEach(({ group, place }) => {
    const existing = byProcedureId.get(group.procedure.id);

    if (existing) {
      existing.places.push(place);
      return;
    }

    byProcedureId.set(group.procedure.id, {
      id: group.procedure.id,
      name: group.procedure.name,
      places: [place],
    });
  });

  return Array.from(byProcedureId.values());
}

export function mergeProcedureGroups(
  current: ProfessionalProcedureGroup[],
  incoming: ProfessionalProcedureGroup[],
): ProfessionalProcedureGroup[] {
  const merged = current.map((group) => ({ ...group, places: [...group.places] }));
  const byId = new Map(merged.map((group) => [group.id, group]));

  incoming.forEach((group) => {
    const existing = byId.get(group.id);

    if (!existing) {
      const copy = { ...group, places: [...group.places] };
      merged.push(copy);
      byId.set(copy.id, copy);
      return;
    }

    const knownPlaceIds = new Set(existing.places.map((place) => place.id));
    existing.places.push(...group.places.filter((place) => !knownPlaceIds.has(place.id)));
  });

  return merged;
}

export async function getProfessionalProceduresFromSearchApi({
  professionalId,
  serviceChannel,
  page = 1,
  limit = PROFESSIONAL_PROCEDURES_PAGE_SIZE,
}: GetProfessionalProceduresParams): Promise<GetProfessionalProceduresResult> {
  const logger = getLogger('getProfessionalProceduresFromSearchApi');

  try {
    const { groups, hasMore } = await findSchedulesByProfessionalId(professionalId, {
      serviceChannel,
      page,
      limit,
    });

    if (groups.length === 0) {
      return { ...EMPTY_RESULT, hasMore };
    }

    const slugs = Array.from(new Set(groups.map((group) => group.procedure.slug).filter(Boolean)));

    const proceduresRequest: Promise<SearchApiProcedure[]> = slugs.length > 0
      ? findProceduresBySlugs(slugs).catch(() => [])
      : Promise.resolve([]);

    const [procedures, facilities, partners, professional] = await Promise.all([
      proceduresRequest,
      hydrateById(uniqueIds(groups, (group) => group.partnerFacilityId), findFacilityById),
      hydrateById(uniqueIds(groups, (group) => group.partnerId), findPartnerById),
      findProfessionalById(professionalId).catch(() => null),
    ]);

    const procedureBySlug = new Map(procedures.map((procedure) => [procedure.slug, procedure]));
    const searchType = serviceChannel === SearchApiServiceChannel.TELEHEALTH
      ? SearchType.ONLINE_CONSULTATION
      : undefined;

    const places = groups.map((group) => ({
      group,
      place: buildPlace({
        group,
        procedure: procedureBySlug.get(group.procedure.slug) ?? toProcedureFallback(group.procedure),
        professional,
        partner: partners.get(group.partnerId ?? '') ?? null,
        facility: facilities.get(group.partnerFacilityId ?? '') ?? null,
        searchType,
      }),
    }));

    return { procedures: groupByProcedure(places), hasMore };
  } catch (error) {
    logger.error('Failed to fetch professional procedures', { professionalId, serviceChannel, page, error });

    throw error;
  }
}
